import type { TrackableProduct } from 'types/TrackableProduct';

import {
  type RetailerOption,
  toWireRetailerOption,
  type WireRetailerOption
} from '~/utils/listings';

import InteractionEvent, {
  type InteractionTargetType
} from './InteractionEvent';

export type RetailerPlacement = 'list' | 'sticky';

export type RetailersPositionScope = 'list' | 'stock_status';

// What retailers[].position is an ordinal within. Web renders one ordering
// across every option, so it is always 'list'; iOS splits the list into
// per-stock-status tabs and restarts position in each. Sent rather than inferred
// from platform so no query has to — see the pagination_mode precedent.
const RETAILERS_POSITION_SCOPE: RetailersPositionScope = 'list';

// v2 adds retailers_position_scope. The bump is what lets a consumer tell "this client
// could not have sent it" from "it should be here and isn't" — a version travels
// with the row, where a rollout date has to be remembered.
const RETAILER_SCHEMA_VERSION = 2;

export type RetailerTargetProperties = {
  placement: RetailerPlacement;
  product_id?: string;
  retailer_id?: string;
  retailers: WireRetailerOption[];
  retailers_position_scope: RetailersPositionScope;
  selected_size?: string;
};

type EventData = {
  gesture?: string;
  guestId?: string;
  isBot: boolean;
  placement: RetailerPlacement;
  product: TrackableProduct;
  retailerId?: string;
  retailers: RetailerOption[];
  selectedSize?: string;
};

class RetailerInteractionEvent extends InteractionEvent<
  EventData,
  RetailerTargetProperties
> {
  protected get targetType(): InteractionTargetType {
    return 'retailer';
  }

  protected get schemaVersion(): number {
    return RETAILER_SCHEMA_VERSION;
  }

  protected getTargetProperties(): RetailerTargetProperties {
    return {
      placement: this.data.placement,
      product_id: this.data.product.product_id?.toString(),
      retailer_id: this.data.retailerId,
      retailers: this.data.retailers.map(toWireRetailerOption),
      retailers_position_scope: RETAILERS_POSITION_SCOPE,
      selected_size: this.data.selectedSize
    };
  }
}

export default RetailerInteractionEvent;
